// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Board-level object recognition and bounding-box computation (spec §6.2).
 *
 * Every bbox here is conservative by construction. Where an approximation is
 * used, the approximation is documented at the point of use and the object is
 * marked `bboxApproximate` so the UI can say so out loud.
 */

import { atoms, get, getAll, getNums, getStr, getStrs, isAtom, isNode, type Node } from '../sexp/index.ts';
import {
  bbox,
  fromPoints,
  pad as padBox,
  union,
  unionAll,
  type BBox,
  type MaybeBBox,
} from './bbox.ts';
import { arcExtent, circleExtent, rotatedBoxExtent, type Point } from './geometry.ts';
import { type LayerTable } from './layers.ts';

/** Board-level tokens the model understands (spec §6.2). */
export const KNOWN_OBJECT_TYPES: ReadonlySet<string> = new Set([
  'footprint',
  'segment',
  'arc',
  'via',
  'zone',
  'group',
  'dimension',
  'image',
  'gr_line',
  'gr_arc',
  'gr_circle',
  'gr_rect',
  'gr_poly',
  'gr_curve',
  'gr_text',
  'gr_text_box',
  'gr_bbox',
  'table',
  'barcode',
  'target',
  // KiCad 10.
  'point',
  // Present in real files, same handling as their gr_ equivalents.
  'gr_vector',
  'generated',
]);

/**
 * Top-level blocks that are global board state and belong to no region
 * (spec §3.7). These are never treated as objects.
 */
export const GLOBAL_BLOCKS: readonly string[] = [
  'general',
  'paper',
  'title_block',
  'layers',
  'setup',
  'properties',
  'embedded_files',
  // KiCad 9 writes `(embedded_fonts no)` as the last top-level child. It is a
  // board setting, not an object: treating it as one gives it no geometry, and
  // an object with no knowable extent is a V8 violation — so every comparison
  // of a KiCad 9 board would fail for a reason that is not about the board.
  'embedded_fonts',
];

/**
 * Tokens that own other objects by UUID reference rather than by containment.
 *
 * `group` is the obvious one; KiCad 9's `generated` (length-tuning meanders)
 * works the same way — it lists the track segments it produced in a `members`
 * block. Both share the §3.6 hazard: splice half of one and the remaining
 * members dangle. Both also need their extent taken from their members, because
 * their own geometry (a tuning pattern's base line) is *smaller* than what they
 * actually own.
 */
export const MEMBER_CONTAINER_TYPES: ReadonlySet<string> = new Set(['group', 'generated']);

/** Header fields describing the writing tool (spec §3.8). */
export const HEADER_FIELDS: readonly string[] = ['version', 'generator', 'generator_version', 'host'];

/**
 * A `(property "K" "V")` at board level is a text variable, but KiCad also
 * writes board-level `(property …)` nodes as a flat list rather than nested in
 * `(properties)`. Both spellings are treated as global state.
 */
export const BOARD_PROPERTY_TOKEN = 'property';

/**
 * An `image` carries a base64 blob whose on-board size depends on its DPI and
 * scale in ways that are not reliably recoverable from the file. Rather than
 * guess small, we claim a generous square around the anchor and flag the object
 * as approximate — erring large per §6.2, and visibly rather than silently.
 */
export const IMAGE_ASSUMED_HALF_SIZE_MM = 25;

export interface ObjectInfo {
  /** Token name, e.g. `segment`. */
  type: string;
  /** Whether the model recognises this token (spec §6.2 "classify as unknown"). */
  known: boolean;
  bbox: MaybeBBox;
  /** True when the box is a documented over-approximation rather than exact. */
  bboxApproximate: boolean;
}

// --- point harvesting ---------------------------------------------------------

const POINT_TOKENS = new Set(['at', 'start', 'end', 'mid', 'center', 'xy', 'pos']);
/** Fill caches are stale derived data and must never influence a bbox (§3.5). */
const FILL_CACHE_TOKENS = new Set(['filled_polygon', 'fill_segments', 'filled_areas']);

/** First two numeric atoms of a node, as a point. */
function pointOf(n: Node): Point | null {
  const nums = atoms(n)
    .map((a) => Number(a.value))
    .filter((v) => Number.isFinite(v));
  if (nums.length < 2) return null;
  return [nums[0]!, nums[1]!];
}

/**
 * Harvest every coordinate-looking child in a subtree. This is the fallback for
 * unknown tokens: "attempt a bounding box from any `at`/`start`/`end`/`xy`
 * children it happens to have" (spec §6.2).
 */
export function harvestPoints(n: Node, out: Point[] = []): Point[] {
  for (const child of n.children) {
    if (!isNode(child)) continue;
    if (FILL_CACHE_TOKENS.has(child.name)) continue;
    if (POINT_TOKENS.has(child.name)) {
      const p = pointOf(child);
      if (p) out.push(p);
    }
    harvestPoints(child, out);
  }
  return out;
}

/** Stroke width, in either the modern `(stroke (width w))` or KiCad 6 `(width w)` form. */
export function strokeWidth(n: Node): number {
  const modern = get(n, 'stroke');
  if (modern) {
    const w = getNums(modern, 'width')[0];
    if (w !== undefined) return w;
  }
  const legacy = getNums(n, 'width')[0];
  return legacy ?? 0;
}

function pointsOf(n: Node, token: string): Point[] {
  const holder = get(n, token);
  if (!holder) return [];
  const out: Point[] = [];
  for (const xy of getAll(holder, 'xy')) {
    const p = pointOf(xy);
    if (p) out.push(p);
  }
  return out;
}

function xy(n: Node, token: string): Point | null {
  const child = get(n, token);
  return child ? pointOf(child) : null;
}

// --- text -------------------------------------------------------------------

/**
 * Conservative text extent.
 *
 * KiCad's stroke font advance is roughly 1.0× the glyph size and never exceeds
 * it by much; we use 1.15× per character horizontally and a full glyph size
 * vertically, which covers ascenders, descenders and stroke thickness.
 *
 * Justification moves the *anchor*, and getting that wrong is what makes a text
 * box under-report. `(justify bottom)` anchors at the baseline, so the glyphs
 * sit above the anchor — a box centred on the anchor misses them entirely. Each
 * justification is handled with a margin in the opposite direction too, because
 * a TrueType face can overshoot its nominal box.
 */
export function textExtent(n: Node, text: string, localOnly = false): BBox {
  const at = get(n, 'at');
  const atNums = at ? getNums(n, 'at') : [];
  const ax = localOnly ? 0 : (atNums[0] ?? 0);
  const ay = localOnly ? 0 : (atNums[1] ?? 0);
  const angle = atNums[2] ?? 0;

  const font = get(get(n, 'effects') ?? n, 'font');
  const sizes = font ? getNums(font, 'size') : [];
  const size = Math.max(sizes[0] ?? 1.27, sizes[1] ?? 1.27, 0.1);

  // Text wraps at newlines. Measuring the whole string as one line is the
  // difference between a 66 mm note and a 957 mm one — a real board carries a
  // 40-line stackup note whose box would otherwise cross every other board on
  // the panel.
  const lines = text.split('\n');
  const longestLine = Math.max(...lines.map((l) => l.length), 1);
  const lineHeight = size * 1.2;
  const blockHeight = Math.max(lines.length, 1) * lineHeight;

  const w = longestLine * size * 1.15;

  const justify = get(get(n, 'effects') ?? n, 'justify');
  const jTokens = justify ? atoms(justify).map((a) => a.value) : [];
  const mirrored = jTokens.includes('mirror');

  // Mirroring reflects the text about its anchor. For centred text — which is
  // the default, and so what almost every back-side designator is — that
  // reflection maps the box onto itself and changes the extent not at all.
  // Treating `mirror` as "could be either side" doubled the width of every
  // back-layer label, which on a real badge put eight test points across a
  // region boundary they sit well inside.
  //
  // Combined with left/right justification the mirrored direction is not
  // something worth being confident about, so that case alone still claims both
  // sides.
  const left = jTokens.includes('left');
  const right = jTokens.includes('right');
  let minX = -w / 2;
  let maxX = w / 2;
  if (left || right) {
    if (mirrored) {
      minX = -w;
      maxX = w;
    } else if (left) {
      minX = 0;
      maxX = w;
    } else {
      minX = -w;
      maxX = 0;
    }
  }

  // Board coordinates are Y-down, so text above the anchor is at negative Y.
  const slack = lineHeight * 0.3;
  let minY = -blockHeight / 2 - slack;
  let maxY = blockHeight / 2 + slack;
  if (jTokens.includes('bottom')) {
    minY = -blockHeight - slack;
    maxY = slack;
  } else if (jTokens.includes('top')) {
    minY = -slack;
    maxY = blockHeight + slack;
  }

  const local = bbox(minX, minY, maxX, maxY);
  return rotatedBoxExtent(local, angle, ax, ay);
}

/**
 * Is this text field hidden?
 *
 * Modern footprints carry BOM metadata as hidden `property` fields — MPN,
 * Description, Manufacturer, distributor part numbers — all anchored at the
 * footprint origin. KiCad never draws them, but they are long: a 42-character
 * "Capacitor MLCC, 100nF, 50V, X7R, 0402, 10%" estimates out to a 61 mm box on
 * an 0402 capacitor, which is ten times the part. Counting them does not make
 * the box conservative, it makes it meaningless — on a real board it put a
 * sixth of all footprints across a region boundary that they nowhere near
 * reach. A hidden field is not drawn and not manufactured, so its true extent
 * is nothing.
 *
 * Handles KiCad 7+ `(hide yes)`, the same inside `(effects …)`, and KiCad 6's
 * bare `hide` atom.
 */
export function isHiddenText(n: Node): boolean {
  const direct = get(n, 'hide');
  if (direct) return (atoms(direct)[0]?.value ?? 'yes') !== 'no';

  const effects = get(n, 'effects');
  if (effects) {
    const nested = get(effects, 'hide');
    if (nested) return (atoms(nested)[0]?.value ?? 'yes') !== 'no';
    if (effects.children.some((c) => isAtom(c) && c.value === 'hide')) return true;
  }
  return n.children.some((c) => isAtom(c) && c.value === 'hide');
}

/**
 * Exact glyph extent from a `(render_cache …)` block, when KiCad has cached one.
 *
 * KiCad 9 caches the outline polygons of TrueType text so it does not have to
 * re-shape the font on every load. Those points are the real thing — far better
 * than any estimate — and for **board-level** text they are in absolute board
 * coordinates, which is what makes them directly usable here.
 *
 * This is deliberately not applied to text inside footprints. No footprint text
 * in the boards available carried a render cache, so the coordinate frame for
 * that case is unverified, and a wrong frame would produce a box the width of
 * the board. Guessing large is only safe when it is still *right*.
 */
export function renderCacheExtent(n: Node): MaybeBBox {
  const cache = get(n, 'render_cache');
  if (cache === undefined) return null;
  return fromPoints(harvestPoints(cache));
}

/**
 * Extend a box to include the node's own `at` anchor.
 *
 * A cached glyph outline is the text's *drawn* extent, and the anchor can fall
 * outside it depending on justification. An object whose box does not contain
 * its own stated position is confusing to click, to zoom to, and to reason about
 * when classifying it against a region — so the anchor is always included.
 */
function withAnchor(n: Node, box: MaybeBBox): MaybeBBox {
  const nums = getNums(n, 'at');
  if (nums.length < 2) return box;
  return union(box, bbox(nums[0]!, nums[1]!, nums[0]!, nums[1]!));
}

/** Half the pen width a text is stroked with, for padding a cached outline. */
export function textThickness(n: Node): number {
  const font = get(get(n, 'effects') ?? n, 'font');
  const t = font ? getNums(font, 'thickness')[0] : undefined;
  return (t ?? 0.15) / 2;
}

// --- footprint --------------------------------------------------------------

const FOOTPRINT_GRAPHIC_TOKENS = new Set([
  'fp_line',
  'fp_arc',
  'fp_circle',
  'fp_rect',
  'fp_poly',
  'fp_curve',
  'fp_text',
  'fp_text_box',
  'property',
  'dimension',
]);

/** Local (unrotated, footprint-origin) extent of one footprint child. */
function footprintChildExtent(child: Node): MaybeBBox {
  const half = strokeWidth(child) / 2;

  switch (child.name) {
    case 'pad': {
      const p = xy(child, 'at');
      if (!p) return null;
      const size = getNums(child, 'size');
      const w = size[0] ?? 0;
      const h = size[1] ?? w;
      // Half-diagonal covers the pad at any rotation and any shape
      // (roundrect, oval, trapezoid, custom primitives inside the size box).
      const r = Math.hypot(w, h) / 2;
      let box: MaybeBBox = bbox(p[0] - r, p[1] - r, p[0] + r, p[1] + r);
      // Custom pads may carry primitives that stick out beyond `size`.
      const primitives = get(child, 'primitives');
      if (primitives) {
        const pts = harvestPoints(primitives).map(([x, y]) => [x + p[0], y + p[1]] as Point);
        box = union(box, fromPoints(pts));
      }
      return box;
    }
    case 'fp_line':
    case 'fp_rect': {
      const a = xy(child, 'start');
      const b = xy(child, 'end');
      if (!a || !b) return null;
      return padBox(fromPoints([a, b]), half);
    }
    case 'fp_arc': {
      const a = xy(child, 'start');
      const m = xy(child, 'mid');
      const b = xy(child, 'end');
      if (a && m && b) return padBox(arcExtent(a, m, b), half);
      return padBox(fromPoints(harvestPoints(child)), half);
    }
    case 'fp_circle': {
      const c = xy(child, 'center');
      const e = xy(child, 'end');
      if (!c || !e) return null;
      return padBox(circleExtent(c[0], c[1], Math.hypot(e[0] - c[0], e[1] - c[1])), half);
    }
    case 'fp_poly':
    case 'fp_curve':
      return padBox(fromPoints(pointsOf(child, 'pts')), half);
    case 'fp_text':
    case 'fp_text_box':
    case 'property': {
      // Hidden metadata is not drawn; counting it wrecks the box (see isHiddenText).
      if (isHiddenText(child)) return null;
      const text = atoms(child).map((a) => a.value).pop() ?? '';
      if (!get(child, 'at')) return null;
      // No render_cache here: inside a footprint its coordinate frame is
      // unverified, and a wrong frame would give a board-wide box.
      return textExtent(child, text, false);
    }
    default:
      return fromPoints(harvestPoints(child));
  }
}

/**
 * Footprint bbox: the union of pad and graphic extents in footprint-local
 * coordinates, then transformed by the footprint's `at` position and rotation.
 * The courtyard is included because it is just another graphic layer.
 *
 * **Approximation (documented per §6.2):** the local union box is rotated as a
 * rectangle rather than rotating each element and re-boxing. That is never
 * smaller than the true rotated extent — it errs large, which is the required
 * direction. It is then padded by {@link FOOTPRINT_PAD_MM} to absorb anything
 * the local pass could not measure (3D models, unusual child tokens).
 */
export const FOOTPRINT_PAD_MM = 0.1;

export function footprintExtent(fp: Node): { box: MaybeBBox; approximate: boolean } {
  const atNums = getNums(fp, 'at');
  const ox = atNums[0] ?? 0;
  const oy = atNums[1] ?? 0;
  const rot = atNums[2] ?? 0;

  const parts: MaybeBBox[] = [];
  let sawAny = false;
  for (const child of fp.children) {
    if (!isNode(child)) continue;
    if (child.name === 'pad' || FOOTPRINT_GRAPHIC_TOKENS.has(child.name)) {
      const e = footprintChildExtent(child);
      if (e) {
        parts.push(e);
        sawAny = true;
      }
    }
  }

  const local = unionAll(parts);
  if (local === null) {
    // No measurable children: fall back to the anchor point, padded. Flagged
    // approximate so it cannot be mistaken for a measured extent.
    return {
      box: bbox(ox - 1, oy - 1, ox + 1, oy + 1),
      approximate: true,
    };
  }
  const rotated = rotatedBoxExtent(local, rot, ox, oy);
  return { box: padBox(rotated, FOOTPRINT_PAD_MM), approximate: !sawAny };
}

// --- board-level dispatch ----------------------------------------------------

/**
 * Bounding box for one board-level object.
 *
 * Group boxes are resolved later, once every member's box is known, so `group`
 * returns null here.
 */
export function objectExtent(n: Node): { box: MaybeBBox; approximate: boolean } {
  const half = strokeWidth(n) / 2;

  switch (n.name) {
    case 'footprint':
      return footprintExtent(n);

    case 'segment': {
      const a = xy(n, 'start');
      const b = xy(n, 'end');
      if (!a || !b) break;
      // Half the track width on each side (spec §6.2).
      return { box: padBox(fromPoints([a, b]), (getNums(n, 'width')[0] ?? 0) / 2), approximate: false };
    }

    case 'arc': {
      const a = xy(n, 'start');
      const m = xy(n, 'mid');
      const b = xy(n, 'end');
      const w = (getNums(n, 'width')[0] ?? 0) / 2;
      if (a && m && b) return { box: padBox(arcExtent(a, m, b), w), approximate: false };
      break;
    }

    case 'via': {
      const p = xy(n, 'at');
      if (!p) break;
      const size = Math.max(...getNums(n, 'size'), getNums(n, 'drill')[0] ?? 0, 0);
      const r = size / 2;
      return { box: bbox(p[0] - r, p[1] - r, p[0] + r, p[1] + r), approximate: false };
    }

    case 'zone': {
      // Outline polygon extent, ignoring the fill cache (spec §6.2).
      const pts: Point[] = [];
      for (const poly of getAll(n, 'polygon')) pts.push(...pointsOf(poly, 'pts'));
      if (pts.length === 0) break;
      return { box: fromPoints(pts), approximate: false };
    }

    case 'gr_line':
    case 'gr_rect':
    case 'gr_bbox':
    case 'gr_vector': {
      const a = xy(n, 'start');
      const b = xy(n, 'end');
      if (!a || !b) break;
      return { box: padBox(fromPoints([a, b]), half), approximate: false };
    }

    case 'gr_arc': {
      const a = xy(n, 'start');
      const m = xy(n, 'mid');
      const b = xy(n, 'end');
      if (a && m && b) return { box: padBox(arcExtent(a, m, b), half), approximate: false };
      break;
    }

    case 'gr_circle': {
      const c = xy(n, 'center');
      const e = xy(n, 'end');
      if (!c || !e) break;
      return {
        box: padBox(circleExtent(c[0], c[1], Math.hypot(e[0] - c[0], e[1] - c[1])), half),
        approximate: false,
      };
    }

    case 'gr_poly':
    case 'gr_curve': {
      const pts = pointsOf(n, 'pts');
      if (pts.length === 0) break;
      // For gr_curve these are Bézier control points; the curve lies inside
      // their convex hull, so the box is conservative.
      return { box: padBox(fromPoints(pts), half), approximate: false };
    }

    case 'gr_text': {
      const text = atoms(n)[0]?.value ?? '';
      // A render cache is KiCad's own glyph geometry: exact, and better than any
      // estimate. Where it exists it is used on its own, padded by the stroke
      // half-width — unioning it with the estimate would just reinstate the
      // estimate's slack, which for mirrored text means claiming both sides of
      // the anchor and a box twice as wide as the text really is.
      const cached = renderCacheExtent(n);
      if (cached !== null) return { box: withAnchor(n, padBox(cached, textThickness(n))), approximate: false };
      return { box: textExtent(n, text), approximate: true };
    }

    case 'gr_text_box': {
      const cached = renderCacheExtent(n);
      if (cached !== null) return { box: withAnchor(n, padBox(cached, textThickness(n))), approximate: false };
      const pts = pointsOf(n, 'pts');
      if (pts.length > 0) return { box: padBox(fromPoints(pts), half), approximate: false };
      const a = xy(n, 'start');
      const b = xy(n, 'end');
      if (a && b) return { box: padBox(fromPoints([a, b]), half), approximate: false };
      const text = atoms(n)[0]?.value ?? '';
      return { box: textExtent(n, text), approximate: true };
    }

    case 'point':
    case 'target': {
      const p = xy(n, 'at');
      if (!p) break;
      const r = Math.max(getNums(n, 'size')[0] ?? 0, 0) / 2 + half;
      return { box: bbox(p[0] - r, p[1] - r, p[0] + r, p[1] + r), approximate: false };
    }

    case 'image': {
      const p = xy(n, 'at');
      if (!p) break;
      const r = IMAGE_ASSUMED_HALF_SIZE_MM * Math.max(getNums(n, 'scale')[0] ?? 1, 1);
      return { box: bbox(p[0] - r, p[1] - r, p[0] + r, p[1] + r), approximate: true };
    }

    case 'group':
      // Resolved from members after every other object is known.
      return { box: null, approximate: false };

    default:
      break;
  }

  // Generic fallback, used for unknown tokens and for known tokens written in a
  // spelling we did not anticipate. Harvesting every coordinate child is
  // conservative: it can only produce a box at least as large as the true one
  // for anything built out of those coordinates.
  const harvested = fromPoints(harvestPoints(n));
  return { box: harvested === null ? null : padBox(harvested, half), approximate: true };
}

// --- footprint metadata ------------------------------------------------------

/** Reference designator, in KiCad 7+ `property` or KiCad 6 `fp_text` form. */
export function footprintReference(fp: Node): string | undefined {
  for (const p of getAll(fp, 'property')) {
    const vals = atoms(p).map((a) => a.value);
    if (vals[0] === 'Reference') return vals[1];
  }
  for (const t of getAll(fp, 'fp_text')) {
    const vals = atoms(t).map((a) => a.value);
    if (vals[0] === 'reference') return vals[1];
  }
  return undefined;
}

export function footprintValue(fp: Node): string | undefined {
  for (const p of getAll(fp, 'property')) {
    const vals = atoms(p).map((a) => a.value);
    if (vals[0] === 'Value') return vals[1];
  }
  for (const t of getAll(fp, 'fp_text')) {
    const vals = atoms(t).map((a) => a.value);
    if (vals[0] === 'value') return vals[1];
  }
  return undefined;
}

/** `(locked yes)` in KiCad 7+, or a bare `locked` atom in KiCad 6. */
export function isLocked(n: Node): boolean {
  const node = get(n, 'locked');
  if (node) return (atoms(node)[0]?.value ?? 'yes') !== 'no';
  return n.children.some((c) => isAtom(c) && c.value === 'locked');
}

/** Group member UUIDs, from `(members "a" "b")`. */
export function groupMembers(n: Node): string[] {
  return getStrs(n, 'members');
}

/** Library identifier, the footprint node's first positional atom. */
export function footprintLibId(fp: Node): string | undefined {
  return atoms(fp)[0]?.value;
}

/** Schematic symbol link (spec §3.3). Stable across copy/paste. */
export function footprintPath(fp: Node): string | undefined {
  return getStr(fp, 'path');
}


/**
 * The net a `(net …)` node names.
 *
 * KiCad 10 dropped the board-level net table: objects now carry the net name
 * directly, as `(net "GND")`, where every earlier version wrote an index into
 * the table, as `(net 12)` — or `(net 12 "GND")` on pads.
 *
 * The two are told apart by whether the atom was *quoted*, not by whether it
 * parses as a number. A net can legitimately be called "12", and guessing from
 * the text would silently read that name as an index into someone else's table.
 */
export function netReference(netNode: Node): { name?: string; index?: number } {
  const values = atoms(netNode);
  const first = values[0];
  if (first === undefined) return {};
  if (first.quoted) return { name: first.value };
  const index = Number(first.value);
  if (!Number.isFinite(index)) return { name: first.value };
  // The pad form carries the name alongside the index.
  const name = values[1]?.value;
  return name === undefined ? { index } : { index, name };
}

/** Subtrees that are stale derived data and hold no net references worth reading. */
const NET_SCAN_SKIP = FILL_CACHE_TOKENS;

/** Every `(net …)` node in a subtree, skipping zone fill caches. */
export function* netNodes(n: Node): Generator<Node> {
  for (const child of n.children) {
    if (!isNode(child)) continue;
    if (NET_SCAN_SKIP.has(child.name)) continue;
    if (child.name === 'net') {
      yield child;
      continue;
    }
    yield* netNodes(child);
  }
}
